// # Auth Provider Component
import React, { useEffect, useCallback, useRef } from "react";
import { auth, functions } from "./firebase";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useAuthStore } from "./state";
import sessionManager from "./session";
import cacheManager from "./cache_manager";
import Cookies from "js-cookie";

function AuthProvider({ children }) {
  const { setUser, resetAuth, initializeUserSession, setLoading, setUserRole } =
    useAuthStore();
  // # Add token refresh lock to prevent race conditions
  const tokenRefreshInProgress = useRef(false);

  // # INTEGRATED COOKIE MANAGEMENT FUNCTIONS

  // # Set authentication tokens with server support
  const setAuthTokens = useCallback(async (idToken, deviceId) => {
    try {
      // # Set auth token cookie
      Cookies.set("authToken", idToken, {
        expires: 7, // 7 days
        secure: true,
        sameSite: "strict",
        path: "/",
      });

      // # Set device ID cookie
      Cookies.set("deviceId", deviceId, {
        expires: 7,
        secure: true,
        sameSite: "strict",
        path: "/",
      });

      // # Set session active cookie
      Cookies.set("sessionActive", "true", {
        expires: 7,
        secure: true,
        sameSite: "strict",
        path: "/",
      });

      return true;
    } catch (error) {
      console.error("Error setting auth cookies:", error);
      return false;
    }
  }, []);

  // # Clear auth cookies with server support
  const clearAuthCookies = useCallback(async () => {
    try {
      Cookies.remove("authToken", { path: "/" });
      Cookies.remove("deviceId", { path: "/" });
      Cookies.remove("sessionActive", { path: "/" });
      return true;
    } catch (error) {
      console.error("Error clearing auth cookies:", error);
      return false;
    }
  }, []);

  // # Check if user has valid auth cookies
  const hasValidAuthCookies = useCallback(() => {
    return !!(
      Cookies.get("authToken") &&
      Cookies.get("deviceId") &&
      Cookies.get("sessionActive")
    );
  }, []);

  // # Generate a secure token for device ID
  const generateToken = useCallback(() => {
    try {
      if (window.crypto && window.crypto.randomUUID) {
        return window.crypto.randomUUID().replace(/-/g, "");
      }
      // Fallback for older browsers
      const array = new Uint8Array(24);
      window.crypto.getRandomValues(array);
      return Array.from(array)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
    } catch (error) {
      console.error("Error generating token:", error);
      // Last resort fallback
      return btoa(Date.now() + Math.random().toString()).replace(/[/+=]/g, "");
    }
  }, []);

  // # Check user status (banned or suspended)
  const checkUserStatus = useCallback(async (uid) => {
    try {
      // # Call the checkUserStatus function
      const checkStatus = httpsCallable(functions, "checkUserStatus");
      const result = await checkStatus({ uid });

      return result.data;
    } catch (error) {
      console.error("Error checking user status:", error);
      return { isBanned: false, isSuspended: false };
    }
  }, []);

  // # Handle sign out
  const handleSignOut = useCallback(async () => {
    try {
      await Promise.all([
        auth.signOut(),
        sessionManager.clearSession(),
        clearAuthCookies(),
        resetAuth(),
      ]);
      return true;
    } catch (error) {
      console.error("Error signing out:", error);
      return false;
    }
  }, [clearAuthCookies, resetAuth]);

  // # Refresh authentication tokens with race condition prevention
  const refreshAuthTokens = useCallback(async () => {
    // # Prevent concurrent refresh attempts
    if (tokenRefreshInProgress.current) {
      return { success: false, error: "Token refresh already in progress" };
    }

    tokenRefreshInProgress.current = true;

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("No authenticated user");
      }

      // # Check if user is banned or suspended
      const userStatus = await checkUserStatus(currentUser.uid);

      if (userStatus.isBanned || userStatus.isSuspended) {
        // # User is banned or suspended, sign them out
        console.log("User is banned or suspended, signing out");
        await handleSignOut();

        tokenRefreshInProgress.current = false;
        return {
          success: false,
          error: userStatus.isBanned ? "Account banned" : "Account suspended",
          status: userStatus,
        };
      }

      // # Force token refresh
      const newIdToken = await currentUser.getIdToken(true);
      const deviceId = generateToken(); // Generate new device ID

      // # Set new tokens
      await setAuthTokens(newIdToken, deviceId);

      tokenRefreshInProgress.current = false;
      return {
        success: true,
        idToken: newIdToken,
      };
    } catch (error) {
      console.error("Error refreshing auth tokens:", error);
      tokenRefreshInProgress.current = false;
      return {
        success: false,
        error: error.message,
      };
    }
  }, [generateToken, setAuthTokens, checkUserStatus, handleSignOut]);

  // # Get user role directly from token claims
  const getUserRoleFromToken = useCallback(async (user) => {
    try {
      // # Force token refresh to ensure we have the latest claims
      await user.getIdToken(true);
      const tokenResult = await user.getIdTokenResult();

      // # Determine role from token claims
      let role = "user";

      if (tokenResult.claims.admin) role = "admin";
      else if (tokenResult.claims.tutor) role = "tutor";
      else if (tokenResult.claims.content_reviewer) role = "content_reviewer";
      else if (tokenResult.claims.financial_manager) role = "financial_manager";
      else if (tokenResult.claims.designer) role = "designer";
      else if (tokenResult.claims.support) role = "support";

      return {
        role,
        isAdmin: !!tokenResult.claims.admin,
        isTutor: !!tokenResult.claims.tutor,
        isContentReviewer: !!tokenResult.claims.content_reviewer,
        isFinancialManager: !!tokenResult.claims.financial_manager,
        isDesigner: !!tokenResult.claims.designer,
        isSupport: !!tokenResult.claims.support,
      };
    } catch (error) {
      console.error("Error getting user role from token:", error);
      return {
        role: "user",
        isAdmin: false,
        isTutor: false,
        isContentReviewer: false,
        isFinancialManager: false,
        isDesigner: false,
        isSupport: false,
      };
    }
  }, []);

  // # Function to refresh user claims when needed (not on every login)
  const refreshUserClaims = useCallback(
    async (user) => {
      try {
        const refreshClaims = httpsCallable(functions, "refreshUserClaims");
        const result = await refreshClaims();

        if (result.data.updated) {
          // # Force token refresh to get updated claims
          await user.getIdToken(true);

          // # Get updated role information
          const roleInfo = await getUserRoleFromToken(user);

          // # Update session manager with new role info
          if (roleInfo.isAdmin) {
            sessionManager.setAdminStatus(true);
          }

          if (roleInfo.isTutor) {
            sessionManager.setTutorStatus(true);
          }

          sessionManager.setUserRole(roleInfo.role);

          return true;
        }
        return false;
      } catch (error) {
        console.error("Error refreshing user claims:", error);
        return false;
      }
    },
    [getUserRoleFromToken]
  );

  // # Handle auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);

      if (user) {
        try {
          // # Check if user is banned or suspended
          const userStatus = await checkUserStatus(user.uid);

          if (userStatus.isBanned || userStatus.isSuspended) {
            // # User is banned or suspended, sign them out
            console.log("User is banned or suspended, signing out");
            await handleSignOut();
            setLoading(false);
            return;
          }

          // # Get user role from token claims
          const roleInfo = await getUserRoleFromToken(user);

          // # Check if this is a page refresh or new login
          const isPageRefresh =
            !sessionManager.getSession() ||
            !sessionManager.getSession().subscriptions;
          console.log("[AuthProvider] Auth state changed", {
            isPageRefresh,
            hasSession: !!sessionManager.getSession(),
            hasSubscriptions: !!sessionManager.getSession()?.subscriptions,
            isTutor: roleInfo.isTutor,
          });

          // # Initialize user session with role info - only force refresh on page reload
          await initializeUserSession(user, isPageRefresh, roleInfo);

          // # Ensure sessionManager has tutor status set properly
          if (roleInfo.isTutor) {
            sessionManager.setTutorStatus(true);
          }

          // # Set user in auth store
          setUser(user);

          // # Set auth tokens in cookies if they don't exist
          if (!hasValidAuthCookies()) {
            const deviceId = generateToken();
            const idToken = await user.getIdToken();
            await setAuthTokens(idToken, deviceId);
          }

          // # No need to clear subscription access cache or load subscription data since everything is open access
          setLoading(false);
        } catch (error) {
          console.error("Error in auth state change handler:", error);
          resetAuth();
          await clearAuthCookies();
          setLoading(false);
        }
      } else {
        // # User is signed out
        resetAuth();
        await clearAuthCookies();
        setLoading(false);
      }
    });

    // # Cleanup subscription
    return () => unsubscribe();
  }, [
    setUser,
    resetAuth,
    initializeUserSession,
    setLoading,
    getUserRoleFromToken,
    clearAuthCookies,
    setAuthTokens,
    hasValidAuthCookies,
    generateToken,
    checkUserStatus,
    handleSignOut,
  ]);

  // # Set up token refresh interval
  useEffect(() => {
    // # Refresh token every 50 minutes to prevent expiration (Firebase tokens last 1 hour)
    const tokenRefreshInterval = setInterval(async () => {
      if (auth.currentUser) {
        const result = await refreshAuthTokens();

        // # If refresh failed due to ban or suspension, redirect to sign-in page
        if (
          !result.success &&
          (result.error === "Account banned" ||
            result.error === "Account suspended")
        ) {
          window.location.href = "/signin";
        }
      }
    }, 50 * 60 * 1000); // 50 minutes

    return () => clearInterval(tokenRefreshInterval);
  }, [refreshAuthTokens]);

  // # Expose refreshUserClaims to the global window object for debugging
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      window.refreshUserClaims = async () => {
        if (auth.currentUser) {
          const refreshed = await refreshUserClaims(auth.currentUser);
          if (refreshed) {
            const roleInfo = await getUserRoleFromToken(auth.currentUser);
            await initializeUserSession(auth.currentUser, true, roleInfo);
            return { success: true, message: "User claims refreshed" };
          }
          return { success: false, message: "No updates needed" };
        }
        return { success: false, message: "No user logged in" };
      };

      // # Also expose token refresh for debugging
      window.refreshAuthTokens = refreshAuthTokens;
    }

    return () => {
      if (process.env.NODE_ENV === "development") {
        delete window.refreshUserClaims;
        delete window.refreshAuthTokens;
      }
    };
  }, [
    refreshUserClaims,
    getUserRoleFromToken,
    initializeUserSession,
    refreshAuthTokens,
  ]);

  return children;
}

export default AuthProvider;
